import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { ArrowLeftRight, Package, Clock, CheckCircle, XCircle, MessageSquare, ChevronRight, Loader2, Inbox, Send, RefreshCw, Image as ImageIcon, User, RotateCcw, Trash2, PlusCircle } from 'lucide-react';
import axios from '../lib/axios';
import { useAuth, API } from '../App';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { DashboardContainer, PageHeader, FilterChips } from '../components/DashboardLayout';
import { toast } from '../utils/toastWithSound';
import CounterOfferModal from '../components/CounterOfferModal';

const TradeProposalsPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('received'); // 'received' or 'sent'
  const [processingId, setProcessingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [highlightedProposalId, setHighlightedProposalId] = useState(null);
  const [counterModalOpen, setCounterModalOpen] = useState(false);
  const [selectedProposalForCounter, setSelectedProposalForCounter] = useState(null);
  const proposalRefs = useRef({});

  // Handle URL param to highlight specific proposal
  useEffect(() => {
    const proposalId = searchParams.get('id');
    if (proposalId) {
      setHighlightedProposalId(proposalId);
      // Auto-clear highlight after 3 seconds
      const timer = setTimeout(() => setHighlightedProposalId(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [searchParams]);

  // Scroll to highlighted proposal when proposals load
  useEffect(() => {
    if (highlightedProposalId && proposals.length > 0) {
      const proposal = proposals.find(p => p.id === highlightedProposalId);
      if (proposal) {
        // Switch to correct tab if needed
        const isReceived = proposal.receiver_id === user?.id;
        setActiveTab(isReceived ? 'received' : 'sent');
        
        // Scroll to the proposal after a short delay
        setTimeout(() => {
          const ref = proposalRefs.current[highlightedProposalId];
          if (ref) {
            ref.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
      }
    }
  }, [highlightedProposalId, proposals, user?.id]);

  useEffect(() => {
    fetchProposals();
  }, [user]);

  const fetchProposals = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/trade-proposals`);
      setProposals(response.data || []);
    } catch (error) {
      console.error('Failed to fetch proposals:', error);
      toast.error('Failed to load trade proposals');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (proposalId, status) => {
    setProcessingId(proposalId);
    try {
      await axios.put(`${API}/trade-proposals/${proposalId}?status=${status}`);
      toast.success(`Proposal ${status}`);
      fetchProposals();
    } catch (error) {
      toast.error(`Failed to ${status} proposal`);
    } finally {
      setProcessingId(null);
    }
  };

  const handleAcceptCounter = async (proposalId) => {
    setProcessingId(proposalId);
    try {
      await axios.post(`${API}/trade-proposals/${proposalId}/accept-counter`);
      toast.success('Counter offer accepted!');
      fetchProposals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept counter offer');
    } finally {
      setProcessingId(null);
    }
  };

  const handleDeleteProposal = async (proposalId) => {
    if (!window.confirm('Are you sure you want to delete this trade proposal?')) return;
    
    setDeletingId(proposalId);
    try {
      await axios.delete(`${API}/trade-proposals/${proposalId}`);
      toast.success('Trade proposal deleted');
      fetchProposals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete proposal');
    } finally {
      setDeletingId(null);
    }
  };

  const handleMakeAnotherOffer = (proposal) => {
    // Navigate to the listing page with a flag to open the trade modal
    navigate(`/listing/${proposal.listing_id}?openTrade=true`);
  };

  const receivedProposals = proposals.filter(p => p.receiver_id === user?.id);
  const sentProposals = proposals.filter(p => p.proposer_id === user?.id);
  const completedProposals = proposals.filter(p => p.status === 'accepted' || p.status === 'rejected');
  const displayedProposals = activeTab === 'received' 
    ? receivedProposals.filter(p => p.status === 'pending' || p.status === 'countered')
    : activeTab === 'sent' 
    ? sentProposals.filter(p => p.status === 'pending' || p.status === 'countered')
    : completedProposals;

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-500/10 text-amber-500">
            <Clock className="w-3 h-3" /> Pending
          </span>
        );
      case 'accepted':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-500">
            <CheckCircle className="w-3 h-3" /> Accepted
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-500/10 text-red-500">
            <XCircle className="w-3 h-3" /> Rejected
          </span>
        );
      case 'countered':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-500/10 text-blue-500">
            <MessageSquare className="w-3 h-3" /> Countered
          </span>
        );
      default:
        return null;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const formatPrice = (price, currency = 'USD') => {
    if (!price) return null;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(price);
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <DashboardContainer>
          <div className="py-16 text-center">
            <ArrowLeftRight className="w-16 h-16 mx-auto mb-4 text-muted-foreground/30" />
            <h1 className="text-2xl font-bold text-foreground mb-2">Sign in to view trade proposals</h1>
            <p className="text-muted-foreground mb-6">You need to be logged in to see your trade proposals</p>
            <Link to="/auth" className="btn-primary">Sign In</Link>
          </div>
        </DashboardContainer>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <DashboardContainer>
        {/* Standardized Header */}
        <PageHeader pageKey="trade-proposals">
          <button
            onClick={fetchProposals}
            className="p-2 bg-secondary hover:bg-secondary/80 rounded-lg transition-colors"
            title="Refresh"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </PageHeader>

        {/* Filter Chips */}
        <FilterChips
          options={[
            { key: 'received', label: 'Received', icon: Inbox, count: loading ? undefined : receivedProposals.filter(p => p.status === 'pending').length },
            { key: 'sent', label: 'Sent', icon: Send, count: loading ? undefined : sentProposals.filter(p => p.status === 'pending').length },
            { key: 'completed', label: 'Completed', icon: CheckCircle, count: loading ? undefined : [...receivedProposals, ...sentProposals].filter(p => p.status === 'accepted' || p.status === 'rejected').length }
          ]}
          activeFilter={activeTab}
          onFilterChange={setActiveTab}
        />

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : displayedProposals.length === 0 ? (
          <div className="text-center py-16 bg-card rounded-2xl border border-border">
            <ArrowLeftRight className="w-16 h-16 mx-auto mb-4 text-muted-foreground/30" />
            <h2 className="text-xl font-semibold text-foreground mb-2">
              {activeTab === 'received' ? 'No trade proposals received' : activeTab === 'sent' ? 'No trade proposals sent' : 'No completed trades'}
            </h2>
            <p className="text-muted-foreground mb-6">
              {activeTab === 'received' 
                ? 'When someone proposes a trade for your listings, it will appear here'
                : 'Browse listings open for trade and propose exchanges'}
            </p>
            <Link 
              to="/marketplace?open_for_trade=true" 
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-xl font-medium hover:bg-primary/90 transition-colors"
            >
              <Package className="w-5 h-5" />
              Find Items to Trade
            </Link>
          </div>
        ) : (
          <div className="grid gap-5">
            {displayedProposals.map(proposal => {
              const isHighlighted = highlightedProposalId === proposal.id;
              const otherUserId = activeTab === 'received' ? proposal.proposer_id : proposal.receiver_id;
              const otherUserName = activeTab === 'received' ? proposal.proposer_name : proposal.receiver_name;
              const otherUserAvatar = activeTab === 'received' ? proposal.proposer_avatar : null;
              
              return (
              <div
                key={proposal.id}
                ref={el => proposalRefs.current[proposal.id] = el}
                className={`bg-card rounded-2xl border-2 overflow-hidden transition-all duration-300 ${
                  isHighlighted 
                    ? 'border-primary shadow-lg shadow-primary/20 ring-2 ring-primary/30' 
                    : 'border-border hover:shadow-lg hover:border-border/80'
                }`}
              >
                {/* Header with user info */}
                <div className="p-4 md:p-5 bg-secondary/30 border-b border-border">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {/* User Avatar */}
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center overflow-hidden">
                        {otherUserAvatar ? (
                          <img src={otherUserAvatar} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <User className="w-5 h-5 text-primary" />
                        )}
                      </div>
                      <div>
                        <p className="font-semibold text-foreground">{otherUserName || 'User'}</p>
                        <p className="text-xs text-muted-foreground">{formatDate(proposal.created_at)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusBadge(proposal.status)}
                      {/* Delete button for sent proposals */}
                      {activeTab === 'sent' && (
                        <button
                          onClick={() => handleDeleteProposal(proposal.id)}
                          disabled={deletingId === proposal.id}
                          className="p-2 text-muted-foreground hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-colors"
                          title="Delete proposal"
                          data-testid="delete-proposal-btn"
                        >
                          {deletingId === proposal.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="p-4 md:p-5">

                  {/* Trade visualization */}
                  <div className="flex flex-col md:flex-row items-stretch gap-4">
                    {/* What they want (the listing) */}
                    <Link
                      to={`/listing/${proposal.listing_id}`}
                      className="flex-1 p-4 bg-secondary/50 rounded-xl hover:bg-secondary transition-colors group"
                    >
                      <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">
                        {activeTab === 'received' ? 'Your listing' : 'They have'}
                      </p>
                      <div className="flex items-center gap-3">
                        <div className="w-16 h-16 rounded-lg bg-secondary flex items-center justify-center flex-shrink-0 overflow-hidden">
                          <ImageIcon className="w-6 h-6 text-muted-foreground" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-foreground line-clamp-2 group-hover:text-primary transition-colors">
                            {proposal.listing_title}
                          </p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                      </div>
                    </Link>

                    {/* Arrow */}
                    <div className="flex items-center justify-center md:py-0 py-2">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg">
                        <ArrowLeftRight className="w-5 h-5 text-white" />
                      </div>
                    </div>

                    {/* What they're offering */}
                    <div className="flex-1 p-4 bg-emerald-500/5 border border-emerald-500/20 rounded-xl">
                      <p className="text-xs text-emerald-600 dark:text-emerald-400 uppercase tracking-wider mb-2">
                        {activeTab === 'received' ? 'They offer' : 'You offered'}
                      </p>
                      
                      {proposal.offered_listing_id && proposal.offered_listing_title && (
                        <Link
                          to={`/listing/${proposal.offered_listing_id}`}
                          className="flex items-center gap-3 mb-2 hover:opacity-80 transition-opacity"
                        >
                          <div className="w-12 h-12 rounded-lg bg-secondary flex items-center justify-center flex-shrink-0 overflow-hidden">
                            <Package className="w-5 h-5 text-muted-foreground" />
                          </div>
                          <p className="font-medium text-foreground line-clamp-1">
                            {proposal.offered_listing_title}
                          </p>
                        </Link>
                      )}
                      
                      {proposal.offered_items_description && (
                        <p className="text-sm text-foreground">
                          {proposal.offered_listing_title ? '+ ' : ''}{proposal.offered_items_description}
                        </p>
                      )}
                      
                      {proposal.offered_value && (
                        <p className="text-sm font-semibold text-emerald-600 dark:text-emerald-400 mt-1">
                          Est. value: {formatPrice(proposal.offered_value)}
                        </p>
                      )}

                      {!proposal.offered_listing_title && !proposal.offered_items_description && (
                        <p className="text-sm text-muted-foreground italic">No details provided</p>
                      )}
                    </div>
                  </div>

                  {/* Message */}
                  {proposal.message && (
                    <div className="mt-4 p-3 bg-secondary/50 rounded-lg">
                      <p className="text-xs text-muted-foreground mb-1">Message:</p>
                      <p className="text-sm text-foreground">{proposal.message}</p>
                    </div>
                  )}

                  {/* Actions for received pending proposals */}
                  {activeTab === 'received' && proposal.status === 'pending' && (
                    <div className="flex flex-wrap items-center gap-3 mt-4 pt-4 border-t border-border">
                      <button
                        onClick={() => handleUpdateStatus(proposal.id, 'accepted')}
                        disabled={processingId === proposal.id}
                        className="flex-1 min-w-[100px] py-2.5 bg-emerald-500 text-white rounded-lg font-medium hover:bg-emerald-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                        data-testid="accept-proposal-btn"
                      >
                        {processingId === proposal.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <CheckCircle className="w-4 h-4" />
                        )}
                        Accept
                      </button>
                      <button
                        onClick={() => {
                          setSelectedProposalForCounter(proposal);
                          setCounterModalOpen(true);
                        }}
                        disabled={processingId === proposal.id}
                        className="flex-1 min-w-[100px] py-2.5 bg-amber-500 text-white rounded-lg font-medium hover:bg-amber-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                        data-testid="counter-proposal-btn"
                      >
                        <RotateCcw className="w-4 h-4" />
                        Counter
                      </button>
                      <button
                        onClick={() => handleUpdateStatus(proposal.id, 'rejected')}
                        disabled={processingId === proposal.id}
                        className="flex-1 min-w-[100px] py-2.5 bg-secondary text-foreground rounded-lg font-medium hover:bg-secondary/80 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                        data-testid="decline-proposal-btn"
                      >
                        <XCircle className="w-4 h-4" />
                        Decline
                      </button>
                      <Link
                        to={`/messages?conversation=${proposal.listing_id}:${proposal.proposer_id}`}
                        className="py-2.5 px-4 bg-secondary text-foreground rounded-lg font-medium hover:bg-secondary/80 transition-colors flex items-center gap-2"
                      >
                        <MessageSquare className="w-4 h-4" />
                        Chat
                      </Link>
                    </div>
                  )}

                  {/* Counter offer display */}
                  {proposal.status === 'countered' && proposal.counter_offer && (
                    <div className="mt-4 pt-4 border-t border-border">
                      <div className="p-4 bg-gradient-to-br from-amber-500/10 to-orange-500/10 rounded-xl border border-amber-500/30">
                        <div className="flex items-center gap-2 mb-3">
                          <RotateCcw className="w-4 h-4 text-amber-500" />
                          <span className="font-semibold text-amber-600 dark:text-amber-400 text-sm">Counter Offer</span>
                          <span className="text-xs text-muted-foreground ml-auto">
                            by {proposal.counter_offer.created_by_name}
                          </span>
                        </div>
                        
                        {proposal.counter_offer.counter_listing_title && (
                          <Link
                            to={`/listing/${proposal.counter_offer.counter_listing_id}`}
                            className="flex items-center gap-3 p-3 bg-card rounded-lg mb-2 hover:bg-card/80 transition-colors"
                          >
                            <div className="w-10 h-10 rounded-lg bg-secondary overflow-hidden flex-shrink-0">
                              {proposal.counter_offer.counter_listing_image ? (
                                <img src={proposal.counter_offer.counter_listing_image} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <Package className="w-full h-full p-2 text-muted-foreground" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-foreground truncate">{proposal.counter_offer.counter_listing_title}</p>
                              {proposal.counter_offer.counter_listing_price && (
                                <p className="text-sm text-amber-600">{formatPrice(proposal.counter_offer.counter_listing_price)}</p>
                              )}
                            </div>
                            <ChevronRight className="w-4 h-4 text-muted-foreground" />
                          </Link>
                        )}
                        
                        {proposal.counter_offer.counter_items_description && (
                          <p className="text-sm text-foreground mb-2">{proposal.counter_offer.counter_items_description}</p>
                        )}
                        
                        {proposal.counter_offer.counter_value && (
                          <p className="text-sm font-semibold text-amber-600 dark:text-amber-400">
                            Est. value: {formatPrice(proposal.counter_offer.counter_value)}
                          </p>
                        )}
                        
                        {proposal.counter_offer.message && (
                          <p className="text-sm text-muted-foreground italic mt-2">"{proposal.counter_offer.message}"</p>
                        )}
                      </div>
                      
                      {/* Actions for counter offer - only show to original proposer */}
                      {activeTab === 'sent' && (
                        <div className="flex items-center gap-3 mt-3">
                          <button
                            onClick={() => handleAcceptCounter(proposal.id)}
                            disabled={processingId === proposal.id}
                            className="flex-1 py-2.5 bg-emerald-500 text-white rounded-lg font-medium hover:bg-emerald-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                            data-testid="accept-counter-btn"
                          >
                            {processingId === proposal.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <CheckCircle className="w-4 h-4" />
                            )}
                            Accept Counter
                          </button>
                          <Link
                            to={`/messages?conversation=${proposal.listing_id}:${proposal.receiver_id}`}
                            className="flex-1 py-2.5 bg-secondary text-foreground rounded-lg font-medium hover:bg-secondary/80 transition-colors flex items-center justify-center gap-2"
                          >
                            <MessageSquare className="w-4 h-4" />
                            Negotiate
                          </Link>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Status message for non-pending (excluding countered which has its own display) */}
                  {proposal.status !== 'pending' && proposal.status !== 'countered' && (
                    <div className="mt-4 pt-4 border-t border-border">
                      <p className="text-sm text-muted-foreground">
                        {proposal.status === 'accepted' && (
                          <span className="text-emerald-500">
                            This trade was accepted. Contact each other to arrange the exchange.
                          </span>
                        )}
                        {proposal.status === 'rejected' && (
                          <span className="text-red-500">
                            This trade proposal was declined.
                          </span>
                        )}
                      </p>
                      {proposal.status === 'accepted' && (
                        <Link
                          to={`/messages?conversation=${proposal.listing_id}:${otherUserId}`}
                          className="inline-flex items-center gap-2 mt-2 text-primary font-medium hover:gap-3 transition-all"
                        >
                          <MessageSquare className="w-4 h-4" />
                          Send a message
                        </Link>
                      )}
                      {/* Make another offer for rejected proposals (only in sent tab) */}
                      {proposal.status === 'rejected' && activeTab === 'sent' && (
                        <button
                          onClick={() => handleMakeAnotherOffer(proposal)}
                          className="inline-flex items-center gap-2 mt-3 px-4 py-2.5 bg-primary text-white font-medium rounded-lg hover:bg-primary/90 transition-colors"
                          data-testid="make-another-offer-btn"
                        >
                          <PlusCircle className="w-4 h-4" />
                          Make Another Offer
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
            })}
          </div>
        )}
      </DashboardContainer>

      <Footer />

      {/* Counter Offer Modal */}
      <CounterOfferModal
        isOpen={counterModalOpen}
        onClose={() => {
          setCounterModalOpen(false);
          setSelectedProposalForCounter(null);
        }}
        proposal={selectedProposalForCounter}
        onSuccess={fetchProposals}
      />
    </div>
  );
};

export default TradeProposalsPage;
